import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ADDRESS_LOCATION_KEYS,
  AddressComponents,
  AddressRecordSource,
  StreetAddress,
  formatStreetAddressFromComponents
} from '#types';
import { fetchRecord } from '@/store/entityRecordsRegistry';
import useGoogleMapsPlaces from './useGoogleMapsPlaces';
import { useAddressSearch } from './useAddressSearch';
import { pulledRecordAddress } from './pulledRecordAddress';
import type { AddressOption } from './types';

/** The components Places never returns, and so the ones that make Google's `formatted` incomplete. */
const RECIPIENT_KEYS = ['recipientName', 'company', 'phone', 'email'] as const;

const LOCATION_KEYS: ReadonlySet<string> = new Set(ADDRESS_LOCATION_KEYS);

/** A stable identity for "this field offers no pull", so the query path is not rebuilt every render. */
const NO_RECORD_SOURCES: readonly AddressRecordSource[] = [];

/**
 * What clearing the search box leaves behind: the location keys and Places' `alternativeData` gone,
 * every other component kept, `formatted` re-derived from the survivors. Cleared keys are omitted
 * rather than written back as `''`, and an address left with nothing collapses to the fully-empty
 * value — a map of empty strings is not an empty address.
 */
const addressWithLocationCleared = (components: AddressComponents): StreetAddress => {
  const kept: AddressComponents = Object.fromEntries(
    Object.entries(components).filter(([key, componentValue]) => !!componentValue && !LOCATION_KEYS.has(key))
  );
  if (Object.keys(kept).length === 0) return { formatted: '', components: {}, alternativeData: {} };
  return {
    formatted: formatStreetAddressFromComponents(kept),
    components: kept,
    alternativeData: {}
  };
};

/**
 * Props for useAddressUI hook
 */
interface UseAddressUIProps {
  /** Current address value (string or structured) */
  value?: string | StreetAddress | null | undefined;
  /** Callback when address changes */
  onChange?: (value: StreetAddress) => void;
  /** The record sources this field type offers. Absent or empty: addresses only. */
  recordSources?: AddressRecordSource[];
  /** When false, the recipient and company lines are neither shown nor pulled. */
  showRecipient?: boolean;
  /** When false, the phone and email lines are neither shown nor pulled. */
  showContact?: boolean;
}

/**
 * Custom hook managing address input state, search, and updates.
 * Handles autocomplete search, selection, manual edits, and sync with external value.
 *
 * `searchValue` is derived, never mirrored: while the user is actively searching
 * it is the typed text, otherwise it is the formatted external value. Blur,
 * select and clear all end the active search, which is what restores the
 * external value — no effect syncs the two.
 *
 * Clearing that box is location-only. It displays the whole postal label, recipient
 * and company lines included, but it only ever searches geography — the contact
 * lines are entered in `DetailedAddressForm` and have no other home, so the "x"
 * and the emptied-then-blurred path both keep them and re-derive `formatted`.
 *
 * The write sequence lives here and nowhere else. Every handler that will call `onChange` claims it
 * once, after its guards and before its first `await`, and every asynchronous writer compares the
 * number it holds before writing — whatever the person did last is what the field holds. A guarded
 * exit, a keystroke and a prop change all leave it alone, so none of them can drop a write that is
 * still on its way. Unmount is the one thing the counter cannot express, so `mayWrite` checks that
 * too. The query sequence is the separate counter inside `useAddressSearch`.
 *
 * @param props - Hook configuration with value, onChange callback and the field type's props
 * @returns Address UI state and handlers
 */
export const useAddressUI = ({
  value: rawValue,
  onChange,
  recordSources,
  showRecipient = true,
  showContact = true
}: UseAddressUIProps) => {
  const { isReady: servicesReady, searchAddresses, getPlaceDetails, endSession } = useGoogleMapsPlaces();
  const [typedValue, setTypedValue] = useState('');
  const [isActivelySearching, setIsActivelySearching] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const writeSequenceRef = useRef(0);

  /** Every writer, synchronous or not, starts here and nowhere else. */
  const claimWrite = useCallback((): number => {
    writeSequenceRef.current += 1;
    return writeSequenceRef.current;
  }, []);

  /**
   * The write fence is per-mount, so a landing that arrives after the control is gone holds a claim
   * nothing can outrank. It is checked alongside the sequence for the same reason `useFieldUI`
   * cancels its own debounced save on unmount: `onChange` there re-arms that timer and the write
   * does reach Firestore, on a document this control no longer shows — where a later mount of the
   * same field may already have saved something newer.
   *
   * The effect body re-arms the flag, and must: `index.tsx` mounts the app in `StrictMode`, which
   * runs the effect, its cleanup and the effect again on one mount. Returning the cleanup alone
   * leaves the flag false on a control that is on screen, and every asynchronous write — a place
   * details fetch, a record read — is then dropped in silence.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  /** A write may land only while this mount still holds the newest claim. */
  const mayWrite = useCallback((seq: number): boolean =>
    isMountedRef.current && writeSequenceRef.current === seq, []);

  /** The components this control shows, and so the only ones a pulled record address may carry. */
  const componentKeys = useMemo((): readonly (keyof AddressComponents)[] => [
    ...ADDRESS_LOCATION_KEYS,
    ...(showRecipient ? (['recipientName', 'company'] as const) : []),
    ...(showContact ? (['phone', 'email'] as const) : [])
  ], [showRecipient, showContact]);

  const sources = recordSources ?? NO_RECORD_SOURCES;
  const { options, isSearching, hasRows, hasQuery, search, abandon } = useAddressSearch({
    recordSources: sources,
    componentKeys,
    searchAddresses
  });

  const addressValue = useMemo(() => {
    if (!rawValue) return { formatted: '', components: {} };
    if (typeof rawValue === 'string') {
      return {
        formatted: rawValue,
        components: { street: rawValue }
      };
    }
    return rawValue as StreetAddress;
  }, [rawValue]);

  const searchValue = isActivelySearching ? typedValue : (addressValue.formatted || '');

  const handleSearch = useCallback((searchText: string) => {
    setTypedValue(searchText);
    setIsActivelySearching(true);
    search(searchText);
  }, [search]);

  const handleClear = useCallback(() => {
    abandon();
    setIsActivelySearching(false);
    claimWrite();
    onChange?.(addressWithLocationCleared(addressValue.components ?? {}));
  }, [abandon, claimWrite, addressValue.components, onChange]);

  /**
   * Places supplies geography only, so its result is merged over `base` rather than replacing it —
   * picking a suggestion after typing a recipient must not discard the recipient. A pulled record
   * address arrives with the location already cleared out of `base`, because merging one address's
   * street with another's city would be a third address nobody entered.
   *
   * `formatted` stays Google's whenever Google's is all we have: an unresolved suggestion carries a
   * full prediction string with `components: {}`, and deriving from that empty map would blank the
   * label. It is recomputed only when the merge contributes something Places cannot know — a
   * recipient, company or contact — which is exactly when Google's string is incomplete.
   *
   * Writing is all it does. `handleSelect` ended the active search synchronously; a landing that
   * ended it again would undo a keystroke the person has since typed over the selection.
   */
  const applySelectedAddress = useCallback((selected: StreetAddress, base: AddressComponents) => {
    const mergedComponents = {
      ...base,
      ...selected.components,
    };
    const carriesRecipient = RECIPIENT_KEYS.some((key) => !!mergedComponents[key]);
    onChange?.({
      formatted: carriesRecipient
        ? formatStreetAddressFromComponents(mergedComponents)
        : selected.formatted,
      components: mergedComponents,
      alternativeData: selected.alternativeData,
    });
  }, [onChange]);

  /**
   * `getPlaceDetails` is invoked before `endSession` in the same synchronous run: the details
   * request reads the session token before its first `await`, so it closes the session its own
   * predictions were billed under, and the rotation then covers both kinds of selection at one site.
   */
  const handleSelect = useCallback(async (_value: string, option: AddressOption) => {
    if (option.optionKind === 'unavailable') return;
    if (option.optionKind === 'record' && option.disabled) return;
    abandon();
    setIsActivelySearching(false);
    setPopoverOpen(false);
    const seq = claimWrite();

    if (option.optionKind === 'places') {
      const details = servicesReady && option.prediction.alternativeData?.placeId
        ? getPlaceDetails(option.prediction.alternativeData.placeId).catch((error) => {
            console.error('Error fetching place details:', error);
            return option.prediction;
          })
        : Promise.resolve(option.prediction);
      endSession();
      const selected = await details;
      if (!mayWrite(seq)) return;
      applySelectedAddress(selected, addressValue.components ?? {});
      return;
    }

    endSession();
    const record = await fetchRecord(option.source.entityId, option.recordId).catch((error) => {
      console.error('Record address pull failed:', error);
      return null;
    });
    if (!mayWrite(seq)) return;
    const pulled = pulledRecordAddress(record?.fields?.[option.source.fieldId]?.value, componentKeys);
    if (!pulled) return;
    applySelectedAddress(pulled, addressWithLocationCleared(addressValue.components ?? {}).components ?? {});
  }, [
    abandon,
    claimWrite,
    mayWrite,
    servicesReady,
    getPlaceDetails,
    endSession,
    componentKeys,
    addressValue.components,
    applySelectedAddress
  ]);

  const handleComponentChange = useCallback((field: keyof NonNullable<StreetAddress['components']>, newValue: string) => {
    const updatedComponents = {
      ...addressValue.components,
      [field]: newValue
    };

    claimWrite();
    onChange?.({
      formatted: formatStreetAddressFromComponents(updatedComponents),
      components: updatedComponents,
      alternativeData: addressValue.alternativeData
    });
  }, [addressValue, claimWrite, onChange]);

  const handleBlur = useCallback(() => {
    abandon();
    setIsActivelySearching(false);

    if (searchValue.length === 0 && addressValue.formatted) {
      claimWrite();
      onChange?.(addressWithLocationCleared(addressValue.components ?? {}));
    }
  }, [abandon, claimWrite, searchValue, addressValue.formatted, addressValue.components, onChange]);

  return {
    addressValue,
    searchValue,
    options,
    isSearching,
    hasRows,
    hasQuery,
    popoverOpen,
    setPopoverOpen,
    handleSearch,
    handleSelect,
    handleComponentChange,
    handleBlur,
    handleClear
  };
};
